import { VariationType, VariationSection } from '@/helpers/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createVariationThunk,
  fetchVariationsThunk,
  updateVariationThunk,
} from '@/redux/thunk/variationThunk';
import { transformJobSections } from '@/helpers/job/objectFormatter';
import {
  createProductPayload,
  transformBackendProductToLineItem,
  calculateSectionTotals,
} from '@/helpers/variation/variationProductMapper';

// Helper function to safely convert value to number
const getSafeNumber = (value: any): number => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// Helper function to get calculation values
const getCalculationValues = (currentItem: any, field: string, value: any) => {
  const safeValue = getSafeNumber(value);
  return {
    quantity:
      field === 'quantity' ? safeValue : getSafeNumber(currentItem.quantity),
    unitPrice:
      field === 'unit_price'
        ? safeValue
        : getSafeNumber(currentItem.unit_price),
    unitCost:
      field === 'unit_cost' ? safeValue : getSafeNumber(currentItem.unit_cost),
    markup: field === 'markup' ? safeValue : getSafeNumber(currentItem.markup),
    discount:
      field === 'discount' ? safeValue : getSafeNumber(currentItem.discount),
    tax: field === 'tax' ? safeValue : getSafeNumber(currentItem.tax),
  };
};

// Helper function to calculate line item total
const calculateLineItemTotal = (
  values: ReturnType<typeof getCalculationValues>,
): number => {
  const { quantity, unitPrice, discount, tax } = values;

  const total = unitPrice * quantity;

  // Ensure result is valid
  return isNaN(total) || !isFinite(total) ? 0 : total;
};

// Helper function to transform backend variation data to frontend structure
const transformBackendVariationData = (
  backendVariations: any[],
): VariationType[] => {
  // Recursive function to transform sections
  const transformSection = (section: any): VariationSection => {
    return {
      id: section.uuid,
      title: section.title,
      description: section.description || '',
      section_type: section.section_type,
      display_order: section.display_order,
      is_active: section.is_active,
      is_approved: section.is_approved,
      parent_section_id: section.parent_section_id,
      column_visibility: {
        product: section.column_visibility?.product || true,
        quantity:
          section.column_visibility?.qty ||
          section.column_visibility?.quantity ||
          true,
        price: section.column_visibility?.price || true,
        tax: section.column_visibility?.tax || false,
        discount: section.column_visibility?.discount || false,
        total: section.column_visibility?.total || true,
      },
      sections: section.sections?.map(transformSection) || [], // Recursive transformation
      lineItems:
        section.products?.map((product: any) =>
          transformBackendProductToLineItem(product),
        ) || [],
    };
  };

  return backendVariations.map((variation) => {
    // Transform sections
    const transformedSections = variation.sections?.map(transformSection) || [];

    // If no sections exist, add an empty section as per the existing logic
    const finalSections =
      transformedSections.length === 0
        ? [createEmptySection()]
        : transformedSections;

    return {
      uuid: variation.uuid,
      title: variation.title,
      description: variation.description,
      date: variation.date,
      created_at: variation.created_at,
      attachments:
        variation.attachments?.map((att: any) => ({
          link: att.link || att.url,
          size: att.size || '',
          name: att.name || '',
        })) || [],
      signature: variation.signature_url
        ? {
            link: variation.signature_url,
            size: '',
            name: 'signature',
          }
        : null,
      sections: finalSections,
    };
  });
};

// Helper function to transform frontend variation data to backend format
const transformFrontendToBackendData = (variation: VariationType): any => {
  // Recursive function to transform sections
  const transformSectionToBackend = (section: VariationSection): any => {
    return {
      uuid:
        section.id.startsWith('empty-section-') ||
        section.id.startsWith('variation-subsection-')
          ? undefined
          : section.id, // Don't send temp IDs
      title: section.title,
      description: section.description,
      section_type: section.section_type,
      display_order: section.display_order,
      is_active: section.is_active,
      is_approved: true,
      parent_section_id: section.parent_section_id,
      column_visibility: {
        product: section.column_visibility.product,
        qty: section.column_visibility.quantity, // Transform quantity back to qty
        price: section.column_visibility.price,
        tax: section.column_visibility.tax,
        discount: section.column_visibility.discount,
        total: section.column_visibility.total,
      },
      products:
        section.lineItems?.map((lineItem: any) =>
          createProductPayload(lineItem, section.id),
        ) || [],
      sections: section.sections?.map(transformSectionToBackend) || [], // Recursive transformation
      ...calculateSectionTotals(section.lineItems || []),
    };
  };

  return {
    title: variation.title,
    description: variation.description,
    date: variation.date,
    sections: variation.sections?.map(transformSectionToBackend) || [],
  };
};

interface VariationState {
  variations: VariationType[];
  persistedVariations: VariationType[];
  currentVariation: VariationType | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  fieldErrors?: Record<string, string[]>;
}

// Helper function to create an empty section
const createEmptySection = (): VariationSection => ({
  id: `empty-section-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  title: 'New Section',
  description: '',
  section_type: 1,
  display_order: 0,
  is_active: true,
  is_approved: false,
  parent_section_id: null,
  column_visibility: {
    product: true,
    quantity: true,
    price: true,
    tax: true,
    discount: true,
    total: true,
  },
  sections: [],
  lineItems: [],
});

const initialVariation: VariationType = {
  title: '',
  description: '',
  created_at: '',
  attachments: [],
  signature: null,
  sections: [],
};

const initialState: VariationState = {
  variations: [],
  persistedVariations: [],
  currentVariation: null,
  status: 'idle',
  error: null,
  fieldErrors: {},
};

const variationSlice = createSlice({
  name: 'variation',
  initialState,
  reducers: {
    // Set current variation
    setCurrentVariation: (state, action: PayloadAction<VariationType>) => {
      state.currentVariation = action.payload;
    },

    // Clear current variation
    clearCurrentVariation: (state) => {
      state.currentVariation = null;
    },

    // Initialize new variation
    initializeNewVariation: (state) => {
      state.currentVariation = {
        ...initialVariation,
        sections: [createEmptySection()],
      };
    },

    // Add new variation to list
    addVariation: (state, action: PayloadAction<VariationType>) => {
      state.variations.push(action.payload);
    },

    // Update variation in list
    updateVariation: (state, action: PayloadAction<VariationType>) => {
      const index = state.variations.findIndex(
        (variation) => variation.uuid === action.payload.uuid,
      );
      if (index !== -1) {
        state.variations[index] = action.payload;
      }
      if (state.currentVariation?.uuid === action.payload.uuid) {
        state.currentVariation = action.payload;
      }
    },

    // Remove variation from list
    removeVariation: (state, action: PayloadAction<string>) => {
      state.variations = state.variations.filter(
        (variation) => variation.uuid !== action.payload,
      );
      if (state.currentVariation?.uuid === action.payload) {
        state.currentVariation = null;
      }
    },

    // Set all variations (with optional transformation)
    setVariations: (state, action: PayloadAction<VariationType[] | any[]>) => {
      // If the payload looks like backend data, transform it first
      if (
        action.payload.length > 0 &&
        action.payload[0].uuid &&
        action.payload[0].sections
      ) {
        state.variations = transformBackendVariationData(action.payload);
      } else {
        state.variations = action.payload as VariationType[];
      }
    },

    // Clear all variations
    clearVariations: (state) => {
      state.variations = [];
      state.currentVariation = null;
    },

    // Update variation field
    updateVariationField: (
      state,
      action: PayloadAction<{ field: keyof VariationType; value: any }>,
    ) => {
      if (state.currentVariation) {
        (state.currentVariation as any)[action.payload.field] =
          action.payload.value;
      }
    },

    // Add section to current variation
    addSection: (state, action: PayloadAction<any>) => {
      if (state.currentVariation) {
        state.currentVariation.sections.push(action.payload);
      }
    },

    // Add sub-section to specific variation by UUID
    addSubSectionToVariation: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        parentSectionId: string;
        section: any;
      }>,
    ) => {
      const variation = state.variations.find(
        (v) => v.uuid === action.payload.variationUuid,
      );
      if (variation) {
        // Helper function to recursively find and add section
        const addSectionToParent = (
          sections: any[],
          parentId: string,
          newSection: any,
        ) => {
          for (const section of sections) {
            if (section.id === parentId) {
              if (!section.sections) {
                section.sections = [];
              }
              section.sections.push(newSection);
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (addSectionToParent(section.sections, parentId, newSection)) {
                return true;
              }
            }
          }
          return false;
        };

        addSectionToParent(
          variation.sections,
          action.payload.parentSectionId,
          action.payload.section,
        );
      }
    },

    // Update section in current variation
    updateSection: (
      state,
      action: PayloadAction<{ sectionId: string; section: any }>,
    ) => {
      if (state.currentVariation) {
        const sectionIndex = state.currentVariation.sections.findIndex(
          (section) => section.id === action.payload.sectionId,
        );
        if (sectionIndex !== -1) {
          state.currentVariation.sections[sectionIndex] =
            action.payload.section;
        }
      }
    },

    // Remove section from current variation
    removeSection: (state, action: PayloadAction<string>) => {
      if (state.currentVariation) {
        state.currentVariation.sections =
          state.currentVariation.sections.filter(
            (section) => section.id !== action.payload,
          );
      }
    },

    // Reorder sections
    reorderSections: (state, action: PayloadAction<any[]>) => {
      if (state.currentVariation) {
        state.currentVariation.sections = action.payload;
      }
    },

    // Add attachment
    addAttachment: (
      state,
      action: PayloadAction<{ link: string; size: string; name: string }>,
    ) => {
      if (state.currentVariation) {
        state.currentVariation.attachments.push(action.payload);
      }
    },

    // Remove attachment
    removeAttachment: (state, action: PayloadAction<number>) => {
      if (state.currentVariation) {
        state.currentVariation.attachments.splice(action.payload, 1);
      }
    },

    // Set signature
    setSignature: (
      state,
      action: PayloadAction<{
        link: string;
        size: string;
        name: string;
      } | null>,
    ) => {
      if (state.currentVariation) {
        state.currentVariation.signature = action.payload;
      }
    },

    // Set loading state
    setLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },

    // Set success state
    setSuccess: (state) => {
      state.status = 'succeeded';
      state.error = null;
    },

    // Set error state
    setError: (state, action: PayloadAction<string>) => {
      state.status = 'failed';
      state.error = action.payload;
    },

    // Set field errors
    setFieldErrors: (
      state,
      action: PayloadAction<Record<string, string[]>>,
    ) => {
      state.fieldErrors = action.payload;
    },

    // Clear errors
    clearErrors: (state) => {
      state.error = null;
      state.fieldErrors = {};
    },

    // Add line item to variation section
    addLineItemToVariationSection: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        sectionId: string;
        lineItem: any;
      }>,
    ) => {
      const { variationUuid, sectionId, lineItem } = action.payload;
      const variation = state.variations.find((v) => v.uuid === variationUuid);
      lineItem.unit_cost = lineItem.cost;
      lineItem.unit_price = lineItem.price;

      // Calculate initial total for the line item
      const values = getCalculationValues(lineItem, '', 0);
      lineItem.total = calculateLineItemTotal(values);
      if (variation) {
        // Helper function to recursively find section and add line item
        const addLineItemToSection = (
          sections: any[],
          targetSectionId: string,
        ): boolean => {
          for (const section of sections) {
            if (section.id === targetSectionId) {
              if (!section.lineItems) {
                section.lineItems = [];
              }
              section.lineItems.push(lineItem);
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (addLineItemToSection(section.sections, targetSectionId)) {
                return true;
              }
            }
          }
          return false;
        };

        addLineItemToSection(variation.sections, sectionId);
      }
    },

    // Remove line item from variation section
    removeLineItemFromVariationSection: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        sectionId: string;
        lineItemId: string;
      }>,
    ) => {
      const { variationUuid, sectionId, lineItemId } = action.payload;
      const variation = state.variations.find((v) => v.uuid === variationUuid);
      if (variation) {
        // Helper function to recursively find section and remove line item
        const removeLineItemFromSection = (
          sections: any[],
          targetSectionId: string,
        ): boolean => {
          for (const section of sections) {
            if (section.id === targetSectionId) {
              if (section.lineItems) {
                section.lineItems = section.lineItems.filter(
                  (item: any) => item.id !== lineItemId,
                );
              }
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (
                removeLineItemFromSection(section.sections, targetSectionId)
              ) {
                return true;
              }
            }
          }
          return false;
        };

        removeLineItemFromSection(variation.sections, sectionId);
      }
    },

    // Update line item in variation section
    updateLineItemInVariationSection: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        sectionId: string;
        lineItemId: string;
        updates: any;
      }>,
    ) => {
      const { variationUuid, sectionId, lineItemId, updates } = action.payload;
      const variation = state.variations.find((v) => v.uuid === variationUuid);
      if (variation) {
        // Helper function to recursively find section and update line item
        const updateLineItemInSection = (
          sections: any[],
          targetSectionId: string,
        ): boolean => {
          for (const section of sections) {
            if (section.id === targetSectionId) {
              if (section.lineItems) {
                const lineItemIndex = section.lineItems.findIndex(
                  (item: any) => item.id === lineItemId,
                );
                if (lineItemIndex !== -1) {
                  const currentItem = section.lineItems[lineItemIndex];
                  let updatedItem = {
                    ...currentItem,
                    ...updates,
                  };

                  // Check if any field that affects total calculation was updated
                  const calculationFields = [
                    'quantity',
                    'unit_price',
                    'markup',
                    'discount',
                    'tax',
                  ];
                  const updatedField = Object.keys(updates)[0]; // Get the first updated field

                  if (calculationFields.includes(updatedField)) {
                    const values = getCalculationValues(
                      currentItem,
                      updatedField,
                      updates[updatedField],
                    );
                    updatedItem.total = calculateLineItemTotal(values);
                  }

                  section.lineItems[lineItemIndex] = updatedItem;
                }
              }
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (updateLineItemInSection(section.sections, targetSectionId)) {
                return true;
              }
            }
          }
          return false;
        };

        updateLineItemInSection(variation.sections, sectionId);
      }
    },

    // Update variation section title
    updateVariationSectionTitle: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        sectionId: string;
        title: string;
      }>,
    ) => {
      const { variationUuid, sectionId, title } = action.payload;
      const variation = state.variations.find((v) => v.uuid === variationUuid);
      if (variation) {
        // Helper function to recursively find and update section title
        const updateSectionTitle = (
          sections: any[],
          targetSectionId: string,
        ): boolean => {
          for (const section of sections) {
            if (section.id === targetSectionId) {
              section.title = title;
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (updateSectionTitle(section.sections, targetSectionId)) {
                return true;
              }
            }
          }
          return false;
        };

        updateSectionTitle(variation.sections, sectionId);
      }
    },

    // Update variation section description
    updateVariationSectionDescription: (
      state,
      action: PayloadAction<{
        variationUuid: string;
        sectionId: string;
        description: string;
      }>,
    ) => {
      const { variationUuid, sectionId, description } = action.payload;
      const variation = state.variations.find((v) => v.uuid === variationUuid);
      if (variation) {
        // Helper function to recursively find and update section description
        const updateSectionDescription = (
          sections: any[],
          targetSectionId: string,
        ): boolean => {
          for (const section of sections) {
            if (section.id === targetSectionId) {
              section.description = description;
              return true;
            }
            if (section.sections && section.sections.length > 0) {
              if (updateSectionDescription(section.sections, targetSectionId)) {
                return true;
              }
            }
          }
          return false;
        };

        updateSectionDescription(variation.sections, sectionId);
      }
    },

    // Add empty section to variations that don't have sections
    addEmptySectionsToVariations: (state) => {
      state.variations = state.variations.map((variation) => {
        if (!variation.sections || variation.sections.length === 0) {
          return {
            ...variation,
            sections: [createEmptySection()],
          };
        }
        return variation;
      });
    },

    // Reset state
    resetState: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    // Handle createVariationThunk
    builder
      .addCase(createVariationThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(createVariationThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.fieldErrors = {};
        // Add the new variation to the list if it exists in the response
        if (action.payload.data) {
          // Transform the new variation data to ensure empty sections are included
          const transformedVariations = transformBackendVariationData([
            action.payload.data,
          ]);
          if (transformedVariations.length > 0) {
            state.variations.push(transformedVariations[0]);
            state.persistedVariations.push(transformedVariations[0]);
          }
        }
      })
      .addCase(createVariationThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to create variation';
        state.fieldErrors = action.payload?.messages || {};
      })
      // Handle fetchVariationsThunk
      .addCase(fetchVariationsThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchVariationsThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        // Set the variations from the API response using the transformation function
        if (action.payload.data) {
          state.variations = transformBackendVariationData(action.payload.data);
        }
        state.persistedVariations = state.variations;
      })
      .addCase(fetchVariationsThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to fetch variations';
      })
      // Handle updateVariationThunk
      .addCase(updateVariationThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(updateVariationThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.error = null;
        state.fieldErrors = {};
        // Update the specific variation in the list if it exists
        if (action.payload.data && action.meta.arg.variationUuid) {
          const variationIndex = state.variations.findIndex(
            (v) => v.uuid === action.meta.arg.variationUuid,
          );
          if (variationIndex !== -1) {
            // Transform the updated variation data and update it in the state
            const updatedVariations = transformBackendVariationData([
              action.payload.data,
            ]);
            if (updatedVariations.length > 0) {
              state.variations[variationIndex] = updatedVariations[0];
              const persistedIndex = state.persistedVariations.findIndex(
                (v) => v.uuid === action.meta.arg.variationUuid,
              );
              if (persistedIndex !== -1) {
                state.persistedVariations[persistedIndex] =
                  updatedVariations[0];
              } else {
                state.persistedVariations.push(updatedVariations[0]);
              }
            }
          }
        }
      })
      .addCase(updateVariationThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.message || 'Failed to update variation';
        state.fieldErrors = action.payload?.messages || {};
      });
  },
});

// Selectors for variation section titles and descriptions
export const selectVariationSectionTitle = (
  state: any,
  variationUuid: string,
  sectionId: string,
): string => {
  const variation = state.variation.variations.find(
    (v: VariationType) => v.uuid === variationUuid,
  );
  if (!variation) return '';

  // Helper function to recursively find section title
  const findSectionTitle = (
    sections: any[],
    targetSectionId: string,
  ): string => {
    for (const section of sections) {
      if (section.id === targetSectionId) {
        return section.title || '';
      }
      if (section.sections && section.sections.length > 0) {
        const found = findSectionTitle(section.sections, targetSectionId);
        if (found !== '') return found;
      }
    }
    return '';
  };

  return findSectionTitle(variation.sections, sectionId);
};

export const selectVariationSectionDescription = (
  state: any,
  variationUuid: string,
  sectionId: string,
): string => {
  const variation = state.variation.variations.find(
    (v: VariationType) => v.uuid === variationUuid,
  );
  if (!variation) return '';

  // Helper function to recursively find section description
  const findSectionDescription = (
    sections: any[],
    targetSectionId: string,
  ): string => {
    for (const section of sections) {
      if (section.id === targetSectionId) {
        return section.description || '';
      }
      if (section.sections && section.sections.length > 0) {
        const found = findSectionDescription(section.sections, targetSectionId);
        if (found !== '') return found;
      }
    }
    return '';
  };

  return findSectionDescription(variation.sections, sectionId);
};

export const {
  setCurrentVariation,
  clearCurrentVariation,
  initializeNewVariation,
  addVariation,
  updateVariation,
  removeVariation,
  setVariations,
  clearVariations,
  updateVariationField,
  addSection,
  addSubSectionToVariation,
  updateSection,
  removeSection,
  reorderSections,
  addAttachment,
  removeAttachment,
  setSignature,
  setLoading,
  setSuccess,
  setError,
  setFieldErrors,
  clearErrors,
  addLineItemToVariationSection,
  removeLineItemFromVariationSection,
  updateLineItemInVariationSection,
  updateVariationSectionTitle,
  updateVariationSectionDescription,
  addEmptySectionsToVariations,
  resetState,
} = variationSlice.actions;

// Export the transformation function for use in components
export { transformFrontendToBackendData };

export default variationSlice.reducer;
